import { ChangeDetectionStrategy, Component, computed, Input, OnInit, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AppNotificationUtilClass } from '@monorepo/services';
import {
  AUTO_PLAN_ORDER_TABLE_MODEL,
  AUTO_PLAN_VIEW_ON_BOARD,
  TAutoPlanOrderRow,
  TLLTimelineModel,
  TTableTreeModel,
  TTimelineModel,
} from '@monorepo/ui';
import {
  TBoardViewMode,
  TREQUEST_GET_OPTIMAL_PLAN_BY_OPL_IDS,
  TRESPONSE_GET_OPTIMAL_PLAN_BY_OPL_IDS,
  TRESPONSE_LLBoardListing,
  TTimelineDateRangeDTO,
  TWithoutPlanVersion,
} from '@monorepo/types';
import { OrderDetailsFacade, TAutoPlanSource } from '../../../../redux-store';
import { AUTO_PLAN_BOARD_MODEL } from './auto-plan-board.model';

export type TAutoPlanOrderModalComponentProps = {
  oplDetails: TWithoutPlanVersion<TREQUEST_GET_OPTIMAL_PLAN_BY_OPL_IDS>;
  source: TAutoPlanSource;
};

export type TAutoPlanOrderModalViewState = {
  title: string;
  summaryTiles: { label: string; value: string | number }[];
  planTable: TTableTreeModel<TAutoPlanOrderRow>;
  unplannedOpls: { oplId: string; reason: string }[];
  hasPlans: boolean;
};

export type TAutoPlanModalTab = 'TABLE' | 'BOARD';

// The Table tab lists every suggestion, so a board that can't be read never blocks confirming the plan.
// IDLE until the Board tab is first opened.
export type TAutoPlanBoardState =
  | { status: 'IDLE' }
  | { status: 'LOADING' }
  | { status: 'FAILED' }
  | {
      status: 'READY';
      llBoard: TRESPONSE_LLBoardListing;
      timelineModel: TLLTimelineModel;
      viewDateRange: TTimelineDateRangeDTO;
      maxDaysToShow: number;
      // The focused suggestion is past the furthest date the LL Board loads.
      isFocusedOutOfRange: boolean;
    };

type TReadyBoard = Extract<TAutoPlanBoardState, { status: 'READY' }>;

export type TAutoPlanOrderModalComponentDismissEmitVal = {
  role: 'SUCCESS' | 'CLOSE';
  data: null;
};

const EMPTY_VALUE = '- -';
const NO_PLAN_REASON = 'No feasible plan found';

export const AUTO_PLAN_ORDER_MODAL_ID = 'auto-plan-order-modal';

@Component({
  selector: 'monorepo-auto-plan-order-modal',
  templateUrl: './auto-plan-order-modal.component.html',
  styleUrls: ['./auto-plan-order-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoPlanOrderModalComponent implements OnInit {
  @Input() set oplDetails(val: TAutoPlanOrderModalComponentProps['oplDetails']) {
    this.#oplData.set(val);
  }

  @Input({ required: true }) source!: TAutoPlanSource;

  readonly #oplData = signal<TAutoPlanOrderModalComponentProps['oplDetails'] | undefined>(undefined);
  readonly #autoPlanDetails = signal<TRESPONSE_GET_OPTIMAL_PLAN_BY_OPL_IDS | undefined>(undefined);
  readonly board = signal<TAutoPlanBoardState>({ status: 'IDLE' });
  readonly activeTab = signal<TAutoPlanModalTab>('TABLE');
  readonly #focusedIndex = signal(0);
  #boardViewType: TBoardViewMode = 'WEEKLY';
  // Only the latest board request may update the board, so quick stepping can't land on an older response.
  #boardRequestCount = 0;

  readonly suggestions = computed(() =>
    AUTO_PLAN_BOARD_MODEL.getSuggestions(this.#autoPlanDetails()?.opl_plans ?? []),
  );
  readonly focusedSuggestion = computed(() => {
    const suggestions = this.suggestions();
    return suggestions.length
      ? { suggestion: suggestions[this.#focusedIndex()], position: this.#focusedIndex() + 1, total: suggestions.length }
      : null;
  });

  readonly viewState = computed<TAutoPlanOrderModalViewState>(() => {
    const autoPlanDetails = this.#autoPlanDetails();
    const oplRequests = this.#oplData()?.oplRequests ?? [];
    const oplPlans = autoPlanDetails?.opl_plans ?? [];

    // opl_plans arrive in delivery date order across every OTL and factory; rows keep that order.
    const rows: TAutoPlanOrderRow[] = this.suggestions().map((suggestion) => ({
      suggestionId: suggestion.id,
      otlId: suggestion.otlId,
      oplId: suggestion.oplId,
      factoryName: suggestion.factoryName || EMPTY_VALUE,
      productType: suggestion.productType,
      line: suggestion.lineName,
      loadStartDate: suggestion.startDate,
      loadEndDate: suggestion.endDate,
      plannedQty: suggestion.qty,
    }));

    const startDates = rows.map((row) => row.loadStartDate).filter(Boolean);
    const endDates = rows.map((row) => row.loadEndDate).filter(Boolean);
    const factoryCount = new Set(oplPlans.map((oplPlan) => oplPlan.factory_id)).size;
    const otlCount = new Set(oplPlans.map((oplPlan) => oplPlan.otl_id)).size;

    return {
      title: `Auto Plan Orders (${oplRequests.length} OPLs)`,
      summaryTiles: [
        { label: 'Total OPLs', value: oplPlans.length || EMPTY_VALUE },
        { label: 'OTLs', value: otlCount || EMPTY_VALUE },
        { label: 'Factories', value: factoryCount || EMPTY_VALUE },
        {
          label: 'Total Load Qty',
          value: rows.reduce((total, row) => total + row.plannedQty, 0) || EMPTY_VALUE,
        },
        { label: 'Plan Start Date', value: startDates.length ? startDates.sort()[0] : EMPTY_VALUE },
        { label: 'Plan End Date', value: endDates.length ? endDates.sort()[endDates.length - 1] : EMPTY_VALUE },
      ],
      planTable: AUTO_PLAN_ORDER_TABLE_MODEL.createModel(rows),
      unplannedOpls: autoPlanDetails ? this.#getUnplannedOpls(oplRequests, autoPlanDetails) : [],
      hasPlans: rows.length > 0,
    };
  });

  constructor(
    private modalController: ModalController,
    private orderDetailsFacade: OrderDetailsFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  async ngOnInit() {
    const data = this.#oplData();
    const res = data ? await this.orderDetailsFacade.getOptimalPlanByOplId(data, this.source) : null;
    if (!res) {
      await this.#dismiss('CLOSE');
      return;
    }
    this.#autoPlanDetails.set(res);
  }

  onTabChange(event: Event) {
    const tab = (event.target as HTMLIonSegmentElement).value as TAutoPlanModalTab;
    if (tab) this.#openTab(tab);
  }

  // The board is read only when someone opens it.
  #openTab(tab: TAutoPlanModalTab) {
    this.activeTab.set(tab);
    if (tab === 'BOARD' && this.board().status === 'IDLE') this.#loadBoard();
  }

  onTableLinkClicked(event: { rowItem: TAutoPlanOrderRow; linkClicked: string }) {
    if (event.linkClicked !== AUTO_PLAN_VIEW_ON_BOARD) return;
    const index = this.suggestions().findIndex((suggestion) => suggestion.id === event.rowItem.suggestionId);
    if (index < 0) return;
    this.activeTab.set('BOARD');
    this.#focus(index);
  }

  onPrevSuggestion() {
    this.#focus(this.#focusedIndex() - 1);
  }

  onNextSuggestion() {
    this.#focus(this.#focusedIndex() + 1);
  }

  // Shows the suggestion from the loaded board when it is there, otherwise reads the board around it.
  #focus(index: number) {
    const suggestion = this.suggestions()[index];
    if (!suggestion) return;
    this.#focusedIndex.set(index);

    const board = this.board();
    if (board.status !== 'READY') {
      this.#loadBoard();
      return;
    }
    const { isInBoard, ...focusedView } = AUTO_PLAN_BOARD_MODEL.getFocusedView(board.timelineModel, suggestion);
    if (!isInBoard) {
      this.#loadBoard();
      return;
    }
    this.board.set({
      ...board,
      ...focusedView,
      timelineModel: AUTO_PLAN_BOARD_MODEL.createModel(board.llBoard, this.suggestions(), suggestion.id),
      isFocusedOutOfRange: false,
    });
  }

  async #loadBoard() {
    const suggestions = this.suggestions();
    const focused = suggestions[this.#focusedIndex()];
    if (!focused) {
      this.board.set({ status: 'FAILED' });
      return;
    }

    const requestCount = ++this.#boardRequestCount;
    this.board.set({ status: 'LOADING' });
    const llBoard = await this.orderDetailsFacade.getAutoPlanBoard(
      AUTO_PLAN_BOARD_MODEL.createRequest(suggestions, this.#boardViewType, focused),
      this.source,
    );
    if (requestCount !== this.#boardRequestCount) return;
    if (!llBoard) {
      this.board.set({ status: 'FAILED' });
      return;
    }

    const timelineModel = AUTO_PLAN_BOARD_MODEL.createModel(llBoard, suggestions, focused.id);
    if (!timelineModel.entities.length) {
      this.board.set({ status: 'FAILED' });
      return;
    }
    const { isInBoard, ...focusedView } = AUTO_PLAN_BOARD_MODEL.getFocusedView(timelineModel, focused);
    this.board.set({ status: 'READY', llBoard, timelineModel, ...focusedView, isFocusedOutOfRange: !isInBoard });
  }

  #setViewDateRange(getRange: (board: TReadyBoard) => TTimelineDateRangeDTO) {
    const board = this.board();
    if (board.status === 'READY') this.board.set({ ...board, viewDateRange: getRange(board) });
  }

  onBoardViewChange(boardView: TTimelineModel['boardView']) {
    this.#boardViewType = boardView.boardViewType;
    this.#loadBoard();
  }

  onBoardNext() {
    this.#setViewDateRange((board) =>
      AUTO_PLAN_BOARD_MODEL.shiftViewDateRange(board.timelineModel, board.viewDateRange, 'NEXT'),
    );
  }

  onBoardPrev() {
    this.#setViewDateRange((board) =>
      AUTO_PLAN_BOARD_MODEL.shiftViewDateRange(board.timelineModel, board.viewDateRange, 'PREV'),
    );
  }

  onBoardToday() {
    this.#setViewDateRange((board) =>
      AUTO_PLAN_BOARD_MODEL.todayViewDateRange(board.timelineModel, board.viewDateRange),
    );
  }

  // Zoom and scroll already moved the timeline; keep the range so a re-render doesn't jump back.
  onBoardViewDateRangeChange(range: TTimelineDateRangeDTO) {
    this.#setViewDateRange(() => range);
  }

  close() {
    this.#dismiss('CLOSE');
  }

  // The planner drops OPLs it can't plan and still returns the rest; list them so they aren't confirmed unnoticed.
  #getUnplannedOpls(
    oplRequests: TREQUEST_GET_OPTIMAL_PLAN_BY_OPL_IDS['oplRequests'],
    autoPlanDetails: TRESPONSE_GET_OPTIMAL_PLAN_BY_OPL_IDS,
  ): TAutoPlanOrderModalViewState['unplannedOpls'] {
    if (autoPlanDetails.failed_opls?.length)
      return autoPlanDetails.failed_opls.map((failedOpl) => ({ oplId: failedOpl.opl_id, reason: failedOpl.reason }));

    return oplRequests
      .filter(
        (oplRequest) =>
          !autoPlanDetails.opl_plans.some(
            (oplPlan) => oplPlan.opl_id === oplRequest.oplId && oplPlan.factory_id === oplRequest.factoryId,
          ),
      )
      .map((oplRequest) => ({ oplId: oplRequest.oplId, reason: NO_PLAN_REASON }));
  }

  #dismiss(role: TAutoPlanOrderModalComponentDismissEmitVal['role']) {
    return this.modalController.dismiss(null, role, AUTO_PLAN_ORDER_MODAL_ID);
  }

  async planOrder() {
    const autoPlanData = this.#autoPlanDetails();
    if (!autoPlanData) return;

    const isSuccess = await this.orderDetailsFacade.loadOplStrips(
      {
        oplPlans: autoPlanData.opl_plans.map((oplPlan) => ({
          oplId: oplPlan.opl_id,
          factoryId: oplPlan.factory_id,
          plans: oplPlan.plans.map((plan) => ({
            loadDate: plan.load_start_date,
            qty: plan.to_be_planned_qty,
            subject: plan.selected_line_subject,
          })),
        })),
      },
      this.source,
    );
    if (isSuccess) {
      const otlCount = new Set(autoPlanData.opl_plans.map((oplPlan) => oplPlan.otl_id)).size;
      const factoryCount = new Set(autoPlanData.opl_plans.map((oplPlan) => oplPlan.factory_id)).size;
      await this.appNotificationUtilClass.presentToastMM(
        'SUCCESS',
        `Auto-plan executed successfully for ${autoPlanData.opl_plans.length} OPL(s) from ${otlCount} OTL(s) across ${factoryCount} factory(s)`,
      );
      await this.#dismiss('SUCCESS');
    }
  }
}
